import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Clock, Car, Route, DollarSign, Download, Search, ChevronDown, RefreshCw,
} from 'lucide-react';
import { getDriverShifts, getDrivers } from '../../services/api';
import {
  format, parseISO, startOfWeek, endOfWeek, startOfMonth, endOfMonth,
} from 'date-fns';
import { sq } from 'date-fns/locale/sq';
import { toast } from 'sonner';

const parseShiftDate = (value) => {
  if (!value) return null;
  try {
    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }
    const str = String(value);
    const d = str.includes('T') ? parseISO(str) : parseISO(`${str}T12:00:00`);
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
};

const normalizeShift = (shift, driversMap = {}) => {
  const driver = driversMap[shift.driver_id] || {};
  const dateSource =
    shift.date ||
    shift.started_at ||
    shift.start_time ||
    shift.created_at;
  const dateObj = parseShiftDate(dateSource);

  return {
    ...shift,
    id: shift.id,
    driver_name: shift.driver_name || driver.name || '—',
    date: dateObj ? format(dateObj, 'yyyy-MM-dd') : null,
    dateObj,
    minutes_online: Number(shift.minutes_online ?? shift.duration_minutes ?? 0),
    km_driven: Number(shift.km_driven ?? 0),
    rides_completed: Number(shift.rides_completed ?? shift.rides_count ?? 0),
    total_earnings: Number(shift.total_earnings ?? shift.system_earnings_all ?? 0),
    vehicle_plate: shift.vehicle_plate || driver.vehicle?.plate || '—',
    status: shift.status || '—',
  };
};

const DriverShiftsPage = () => {
  const [rawShifts, setRawShifts] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('all');
  const [selectedDriver, setSelectedDriver] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const driversMap = useMemo(() => {
    const map = {};
    (Array.isArray(drivers) ? drivers : []).forEach((d) => {
      if (d?.id) map[d.id] = d;
    });
    return map;
  }, [drivers]);

  const shifts = useMemo(
    () => (Array.isArray(rawShifts) ? rawShifts : []).map((s) => normalizeShift(s, driversMap)),
    [rawShifts, driversMap],
  );

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [shiftsData, driversData] = await Promise.all([
        getDriverShifts(selectedDriver !== 'all' ? selectedDriver : null),
        getDrivers(),
      ]);
      setRawShifts(Array.isArray(shiftsData) ? shiftsData : []);
      setDrivers(Array.isArray(driversData) ? driversData : []);
    } catch (error) {
      console.error('Error fetching shifts:', error);
      toast.error(error.response?.data?.detail || 'Gabim në ngarkimin e turneve');
      setRawShifts([]);
    } finally {
      setLoading(false);
    }
  }, [selectedDriver]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredShifts = useMemo(() => {
    let filtered = [...shifts];
    const now = new Date();

    if (dateRange === 'today') {
      const today = format(now, 'yyyy-MM-dd');
      filtered = filtered.filter((s) => s.date === today);
    } else if (dateRange === 'week') {
      const start = startOfWeek(now, { weekStartsOn: 1 });
      const end = endOfWeek(now, { weekStartsOn: 1 });
      filtered = filtered.filter((s) => s.dateObj && s.dateObj >= start && s.dateObj <= end);
    } else if (dateRange === 'month') {
      const start = startOfMonth(now);
      const end = endOfMonth(now);
      filtered = filtered.filter((s) => s.dateObj && s.dateObj >= start && s.dateObj <= end);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (s) =>
          s.driver_name?.toLowerCase().includes(term) ||
          s.vehicle_plate?.toLowerCase().includes(term),
      );
    }

    return filtered.sort((a, b) => {
      const ta = a.dateObj?.getTime() ?? 0;
      const tb = b.dateObj?.getTime() ?? 0;
      return tb - ta;
    });
  }, [shifts, dateRange, searchTerm]);

  const totals = useMemo(
    () =>
      filteredShifts.reduce(
        (acc, shift) => ({
          minutes: acc.minutes + (shift.minutes_online || 0),
          km: acc.km + (shift.km_driven || 0),
          rides: acc.rides + (shift.rides_completed || 0),
          earnings: acc.earnings + (shift.total_earnings || 0),
        }),
        { minutes: 0, km: 0, rides: 0, earnings: 0 },
      ),
    [filteredShifts],
  );

  const formatDuration = (minutes) => {
    const m = Number(minutes) || 0;
    if (!m) return '0h';
    const hours = Math.floor(m / 60);
    const mins = m % 60;
    return `${hours}h${mins > 0 ? ` ${mins}m` : ''}`;
  };

  const formatPrice = (amount) =>
    Number(amount || 0).toLocaleString('sq-AL', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

  const formatShiftDateLabel = (shift) => {
    if (!shift.dateObj) return '—';
    try {
      return format(shift.dateObj, 'dd MMM yyyy', { locale: sq });
    } catch {
      return shift.date || '—';
    }
  };

  const statusLabel = (status) => {
    const map = {
      closed: 'Mbyllur',
      pending_verification: 'Në verifikim',
      flagged: 'Flagged',
      active: 'Aktiv',
      unknown: '—',
    };
    return map[status] || status;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Turnet e Shoferëve</h1>
          <p className="text-gray-500 text-sm mt-1">
            Historiku i turneve dhe mbylljeve të shoferëve
          </p>
        </div>
        <button
          type="button"
          onClick={fetchData}
          className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-black rounded-lg font-semibold hover:bg-yellow-400 transition"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Rifresho
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <p className="text-xs text-gray-500">Orë Totale</p>
          <p className="text-xl font-bold text-gray-900">{formatDuration(totals.minutes)}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <p className="text-xs text-gray-500">KM Totale</p>
          <p className="text-xl font-bold text-gray-900">{formatPrice(totals.km)} km</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <p className="text-xs text-gray-500">Udhëtime</p>
          <p className="text-xl font-bold text-gray-900">{totals.rides}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <p className="text-xs text-gray-500">Të Ardhura</p>
          <p className="text-xl font-bold text-gray-900">{formatPrice(totals.earnings)} L</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
          {[
            { id: 'all', label: 'Të gjitha' },
            { id: 'today', label: 'Sot' },
            { id: 'week', label: 'Java' },
            { id: 'month', label: 'Muaji' },
          ].map((range) => (
            <button
              key={range.id}
              type="button"
              onClick={() => setDateRange(range.id)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${
                dateRange === range.id
                  ? 'bg-yellow-500 text-black'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {range.label}
            </button>
          ))}
        </div>

        <div className="relative">
          <select
            value={selectedDriver}
            onChange={(e) => setSelectedDriver(e.target.value)}
            className="appearance-none bg-white border border-gray-300 text-gray-900 px-4 py-2 pr-10 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
          >
            <option value="all">Të gjithë Shoferët</option>
            {drivers.map((driver) => (
              <option key={driver.id} value={driver.id}>
                {driver.name}
              </option>
            ))}
          </select>
          <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>

        <div className="flex-1 min-w-[200px] relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Kërko sipas emrit ose targës..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-white border border-gray-300 text-gray-900 pl-10 pr-4 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
          />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px]">
            <thead className="bg-gray-50">
              <tr className="border-b border-gray-200">
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Shoferi</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Data</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Automjeti</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase">Kohë</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase">KM</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase">Udhëtime</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Të Ardhura</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase">Statusi</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="text-center py-12 text-gray-500">
                    <RefreshCw className="w-8 h-8 mx-auto mb-2 animate-spin text-yellow-500" />
                    Duke ngarkuar...
                  </td>
                </tr>
              ) : filteredShifts.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-12 text-gray-500">
                    <Clock className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    Nuk ka turne për këtë filtër
                  </td>
                </tr>
              ) : (
                filteredShifts.map((shift) => (
                  <tr key={shift.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{shift.driver_name}</td>
                    <td className="px-4 py-3 text-gray-600">{formatShiftDateLabel(shift)}</td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                        {shift.vehicle_plate}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-blue-600">
                      {formatDuration(shift.minutes_online)}
                    </td>
                    <td className="px-4 py-3 text-center text-green-600">
                      {shift.km_driven.toFixed(1)} km
                    </td>
                    <td className="px-4 py-3 text-center font-semibold text-yellow-600">
                      {shift.rides_completed}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900">
                      {formatPrice(shift.total_earnings)} L
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                        {statusLabel(shift.status)}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DriverShiftsPage;
